import { Link } from 'react-router-dom';
import { ErrorAlert, useApi } from '@pryme/ui';
import { CodeBlock } from '../components/CodeBlock';
import { RedirectEditor } from '../components/RedirectEditor';
import { useServiceOutlet } from '../components/ServiceLayout';
import { pretty } from '../lib/files';
import { canAdmin } from '../lib/types';

interface Discovery {
  issuer: string;
  authorization_endpoint: string;
  token_endpoint: string;
  scopes_supported: string[];
  claims_supported: string[];
  code_challenge_methods_supported: string[];
  token_endpoint_auth_methods_supported: string[];
  subject_types_supported: string[];
  [k: string]: unknown;
}

export function Redirects() {
  const { service: s, reload } = useServiceOutlet();
  const disco = useApi<Discovery>('/.well-known/openid-configuration');
  const d = disco.data;
  const redirect = s.redirectUris[0] ?? '<registered redirect URI>';
  const authUrl = `${d?.authorization_endpoint ?? `${s.apiBase}/authorize`}?response_type=code&client_id=${s.id}&redirect_uri=${encodeURIComponent(redirect)}&scope=openid&claims=${encodeURIComponent(s.claimConfig.requested.join(' '))}&state=<≥16 chars>&nonce=<≥16 chars>&code_challenge=<S256(verifier)>&code_challenge_method=S256`;
  return (
    <>
      <section className="p-card" aria-labelledby="r-h">
        <h2 id="r-h">Redirect URIs</h2>
        <RedirectEditor service={s} canEdit={canAdmin(s.role)} onSaved={() => void reload()} />
        <p className="p-small p-muted" style={{ marginTop: '0.75rem' }}>
          Logout / post-logout redirect URIs are not part of the v0.1 protocol: Pryme does not hold your session. End your own session, and optionally link users to the Pryme portal to disconnect your service.
        </p>
      </section>

      <section className="p-card" aria-labelledby="o-h">
        <h2 id="o-h">OAuth 2.0 and OIDC-compatible surface</h2>
        <ul className="p-small">
          <li>Authorization Code flow with <strong>PKCE S256 required</strong> (even for confidential clients), plus <code>state</code> and <code>nonce</code> (16–256 chars each).</li>
          <li><code>client_id</code> = your service ID <code>{s.id}</code>. The <code>redirect_uri</code> must match a registered URI exactly; unregistered URIs get an error page, never a redirect.</li>
          <li>The callback carries only <code>code</code>, <code>state</code> and <code>iss</code> (mix-up protection). Codes are single-use and live 60 s.</li>
          <li>Token endpoint authentication: <code>pryme_request_signature_v1</code>. Your backend signs <code>POST /token</code> with its signing key; no client secret travels on the wire.</li>
          <li>Subjects are <strong>pairwise</strong>: a stable opaque ID for your service only. No handle, root key, DOB or eID subject is ever included.</li>
          <li>Request claims with <code>claims=human.verified age.over18</code> or scopes <code>claim:age.over18</code>. The default set comes from <Link to="../claims">Claims & consent</Link>.</li>
        </ul>
        <CodeBlock label="Authorization request (built for you by pryme.beginLogin())" code={authUrl.replace(/&/g, '\n  &')} />
        <ErrorAlert error={disco.error} />
        {d && (
          <>
            <h3>Scopes</h3>
            <p>{d.scopes_supported.map((x) => <code key={x} className="p-mono-chip" style={{ marginRight: 4 }}>{x}</code>)}</p>
            <details>
              <summary>Discovery document (<code>/.well-known/openid-configuration</code>)</summary>
              <CodeBlock code={pretty(d)} />
            </details>
          </>
        )}
      </section>
    </>
  );
}
